/**
 * Middleware for Azure AD B2C authentication protection
 * This ensures that the user is authenticated with Azure AD B2C before accessing protected routes
 */
import { watch } from 'vue';
import { useRuntimeConfig } from '#app';
import { useAzureAdAuth } from '~/composables/useAzureAdAuth';
import { refreshTokenDirect } from '~/auth/refreshToken';

// Debug flags
const DEBUG_MIDDLEWARE = true;

/**
 * Helper function to check token expiration
 * @param user The user object to check
 * @returns An object with expiration details
 */
function checkTokenExpiration(user: unknown) {
  if (!user) return { isExpired: true, timeRemaining: 0, expiryDate: null };

  const userObj = user as { expires_at?: number; expired?: boolean; };
  const tokenExpiryTime = userObj.expires_at ? userObj.expires_at * 1000 : 0;
  const currentTime = Date.now();
  const tokenTimeRemaining = tokenExpiryTime - currentTime;
  const isExpired = userObj.expired || tokenTimeRemaining <= 0;
  const expiryDate = tokenExpiryTime ? new Date(tokenExpiryTime) : null;

  return {
    isExpired,
    timeRemaining: tokenTimeRemaining,
    expiryDate
  };
}

export default defineNuxtRouteMiddleware(async (to, _from) => {
  // Log entry point
  if (DEBUG_MIDDLEWARE) {
    console.log(`[MIDDLEWARE] Generic auth - START`, {
      path: to.fullPath,
      timestamp: new Date().toISOString()
    });
  }

  // Skip middleware in server-side rendering
  if (import.meta.server) {
    if (DEBUG_MIDDLEWARE) console.log(`[MIDDLEWARE] Generic auth - Server side, skipping`);
    return;
  }

  // Respect runtime flag to disable auth for testing
  const config = useRuntimeConfig();
  const authEnabled = String(config.public.authEnabled).toLowerCase() === 'true';
  if (!authEnabled) {
    if (DEBUG_MIDDLEWARE) console.log(`[MIDDLEWARE] Generic auth - Auth disabled via config, skipping`);
    return;
  }

  // Get the authentication composable
  const azureAdAuth = useAzureAdAuth();

  // Initialize auth if not already initialized
  azureAdAuth.initialize();
  if (DEBUG_MIDDLEWARE) console.log(`[MIDDLEWARE] Generic auth - Initialized`);

  // Check authentication status
  const { isAuthenticated, isLoading, error, user } = azureAdAuth;

  // Perform a detailed token expiration check
  const tokenStatus = user.value ? checkTokenExpiration(user.value) : { isExpired: true, timeRemaining: 0, expiryDate: null };

  if (DEBUG_MIDDLEWARE) {
    console.log(`[MIDDLEWARE] Generic auth - Auth status:`, {
      isAuthenticated: isAuthenticated.value,
      isLoading: isLoading.value,
      hasError: !!error.value,
      hasUser: !!user.value,
      userExpired: user.value?.expired,
      tokenStatus: {
        isExpired: tokenStatus.isExpired,
        timeRemaining: Math.round(tokenStatus.timeRemaining / 1000) + 's',
        expiryDate: tokenStatus.expiryDate?.toISOString()
      }
    });
  }

  // If still loading, wait a moment
  if (isLoading.value) {
    // Wait for loading to complete (with timeout)
    try {
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Authentication check timed out'));
        }, 5000);

        const unwatch = watch(isLoading, (loading: boolean) => {
          if (!loading) {
            clearTimeout(timeout);
            unwatch();
            resolve();
          }
        });
      });
    } catch (err) {
      console.error('Authentication loading timeout:', err);
      // Continue with best effort - if we're loading too long, assume not authenticated
    }
  }

  // If there was an authentication error, redirect to error page
  if (error.value) {
    console.error('Authentication error:', error.value);
    return navigateTo('/auth/error?error=' + encodeURIComponent(error.value.message));
  }  // If not authenticated, try to refresh the token first before redirecting to login
  if (!isAuthenticated.value) {
    if (DEBUG_MIDDLEWARE) console.log(`[MIDDLEWARE] Generic auth - Not authenticated, checking for user and tokens`);

    let shouldAttemptRefresh = false;

    // Check if user exists and if we have either a refresh token or access token
    if (user.value) {
      // Use our helper to get detailed token status
      const tokenStatus = checkTokenExpiration(user.value);
      console.log(`[MIDDLEWARE] Generic auth - Detailed token status:`, {
        isExpired: tokenStatus.isExpired,
        timeRemaining: Math.round(tokenStatus.timeRemaining / 1000) + 's',
        expiryDate: tokenStatus.expiryDate?.toISOString()
      });

      if (user.value.refresh_token) {
        console.log(`[MIDDLEWARE] Generic auth - User has refresh token, will attempt refresh`);
        shouldAttemptRefresh = true;
      } else if (user.value.access_token) {
        console.log(`[MIDDLEWARE] Generic auth - User has access token but no refresh token`);

        // If token is not expired according to our detailed check, we might not need a refresh
        if (!tokenStatus.isExpired) {
          console.log(`[MIDDLEWARE] Generic auth - Access token is still valid according to detailed check, skipping refresh`);
          if (DEBUG_MIDDLEWARE) console.log(`[MIDDLEWARE] Generic auth - COMPLETE - user is authenticated with valid token`);
          return; // Valid token, no need to redirect
        } else {
          console.log(`[MIDDLEWARE] Generic auth - Access token is expired according to detailed check, will attempt refresh`);
          shouldAttemptRefresh = true;
        }
      }
    }

    // If we have some kind of token to try refreshing
    if (shouldAttemptRefresh) {
      console.log(`[MIDDLEWARE] Generic auth - Attempting token refresh...`);
      try {
        // Try to refresh the token with a timeout
        console.log(`[MIDDLEWARE] Generic auth - Calling refreshTokenDirect()`);

        // Create a timeout promise
        const timeoutPromise = new Promise<null>((_, reject) => {
          setTimeout(() => reject(new Error('Token refresh timeout')), 8000);
        });

        // Race the refresh against the timeout
        const newToken = await Promise.race([
          refreshTokenDirect(),
          timeoutPromise
        ]);

        console.log(`[MIDDLEWARE] Generic auth - refreshTokenDirect result:`, !!newToken);

        if (newToken) {
          console.log(`[MIDDLEWARE] Generic auth - Token refreshed successfully, continuing navigation`);
          // Force refresh the authentication state to reflect the new token
          console.log(`[MIDDLEWARE] Generic auth - Refreshing auth state`);

          // Use a timeout for refreshAuthState to prevent hanging
          try {
            const refreshTimeout = new Promise<void>((_, reject) => {
              setTimeout(() => reject(new Error('Auth state refresh timeout')), 5000);
            });

            await Promise.race([
              azureAdAuth.refreshAuthState(),
              refreshTimeout
            ]);

            console.log(`[MIDDLEWARE] Generic auth - Auth state refreshed successfully`);
          } catch (refreshStateError) {
            console.error(`[MIDDLEWARE] Generic auth - Error refreshing auth state:`, refreshStateError);
            // Continue anyway, we at least have a new token
          }

          // Get updated user after refresh
          let updatedUser;
          try {
            updatedUser = await azureAdAuth.getManager()?.getUser();
            console.log(`[MIDDLEWARE] Generic auth - Updated user:`, !!updatedUser, updatedUser?.expired);

            if (updatedUser) {
              // Use our helper to get detailed token status
              const tokenStatus = checkTokenExpiration(updatedUser);
              console.log(`[MIDDLEWARE] Generic auth - Updated token status:`, {
                isExpired: tokenStatus.isExpired,
                timeRemaining: Math.round(tokenStatus.timeRemaining / 1000) + 's',
                expiryDate: tokenStatus.expiryDate?.toISOString()
              });
            }
          } catch (getUserError) {
            console.error(`[MIDDLEWARE] Generic auth - Error getting updated user:`, getUserError);
          }

          // Double check token validity - if token is still valid, continue with navigation
          if (updatedUser && !updatedUser.expired) {
            if (DEBUG_MIDDLEWARE) console.log(`[MIDDLEWARE] Generic auth - COMPLETE - continuing with refreshed token`);
            return;
          } else if (updatedUser) {
            // Get detailed token status
            const tokenStatus = checkTokenExpiration(updatedUser);

            if (!tokenStatus.isExpired) {
              console.log(`[MIDDLEWARE] Generic auth - Token is valid according to detailed check, continuing`);
              if (DEBUG_MIDDLEWARE) console.log(`[MIDDLEWARE] Generic auth - COMPLETE - continuing with valid token`);
              return;
            }
            console.log(`[MIDDLEWARE] Generic auth - Token is still shown as expired after refresh, will try one more state refresh`);

            // One more attempt to refresh the auth state
            try {
              await azureAdAuth.refreshAuthState();
              // Check isAuthenticated directly
              if (isAuthenticated.value) {
                if (DEBUG_MIDDLEWARE) console.log(`[MIDDLEWARE] Generic auth - COMPLETE - continuing after final refresh check`);
                return;
              }
            } catch (finalRefreshError) {
              console.error(`[MIDDLEWARE] Generic auth - Final auth state refresh failed:`, finalRefreshError);
            }

            console.log(`[MIDDLEWARE] Generic auth - Token refresh didn't result in valid authentication state, redirecting to login`);
          } else {
            // We got a token but couldn't verify the user, try to continue anyway
            console.log(`[MIDDLEWARE] Generic auth - Got new token but couldn't verify user object, attempting to continue`);
            if (DEBUG_MIDDLEWARE) console.log(`[MIDDLEWARE] Generic auth - COMPLETE - continuing with new token without user verification`);
            return;
          }
        } else {
          console.log(`[MIDDLEWARE] Generic auth - Token refresh failed, redirecting to login`);
        }
      } catch (refreshError) {
        console.error(`[MIDDLEWARE] Generic auth - Error refreshing token:`, refreshError);

        // Even though refresh failed, let's check if we might still have a valid session
        try {
          // Force a final auth state refresh
          await azureAdAuth.refreshAuthState();

          // Get the latest user
          const finalUser = await azureAdAuth.getManager()?.getUser();

          // If we somehow have a valid user despite the refresh error
          if (finalUser && !finalUser.expired && finalUser.access_token) {
            console.log(`[MIDDLEWARE] Generic auth - Despite refresh error, found valid user session`);
            if (DEBUG_MIDDLEWARE) console.log(`[MIDDLEWARE] Generic auth - COMPLETE - continuing with existing valid token`);
            return;
          }
        } catch (finalCheckError) {
          console.error(`[MIDDLEWARE] Generic auth - Error during final auth check:`, finalCheckError);
        }
      }
    }

    // If we reach here, either there's no refresh token, no user, or refresh failed
    // Store the original destination for after login
    try {
      sessionStorage.setItem('authRedirect', to.fullPath);
      console.log(`[MIDDLEWARE] Generic auth - Stored redirect path:`, to.fullPath);
    } catch (storageError) {
      console.error(`[MIDDLEWARE] Generic auth - Error storing redirect path:`, storageError);
    }

    // Redirect to login page
    console.log(`[MIDDLEWARE] Generic auth - Redirecting to login`);
    return navigateTo('/auth/login');
  }

  if (DEBUG_MIDDLEWARE) console.log(`[MIDDLEWARE] Generic auth - COMPLETE - user is authenticated`);
});
